import { Button } from '@heidi/design-system/button';
import { Input } from '@heidi/design-system/input';
import { type Editor } from '@tiptap/react';
import { useId } from 'react';
import { useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';
import { normalizeLinkHref } from '../core';

// No zod resolver: a link href has no shape to validate up front. Protocol
// safety is owned by `@tiptap/extension-link` (`setLink` rejects anything
// outside its allowed-protocol list), so we defer to it and surface the
// rejection rather than duplicating an allowlist here.
type LinkForm = { href: string };

export interface LinkEditorPopoverProps {
  editor: Editor;
  /** Existing link href under the selection, or '' when inserting a new link. */
  initialHref: string;
  /** Whether the current selection already carries a link mark. */
  hasLink: boolean;
  onClose: () => void;
}

/**
 * Link insert / edit / remove form (6.5's link control; moved wave B-3).
 * Applies to the whole link mark via `extendMarkRange` so a partial selection
 * still edits the full link. An empty href submission removes the link.
 *
 * The app's `ui/form` wrapper layer (a 235-importer Radix convenience tree
 * that is not a design-system door) is lawfully reimplemented here over
 * react-hook-form directly — same DOM shapes, classes, ids and aria wiring
 * (`aria-invalid` + `aria-describedby` onto the input; the message paragraph)
 * — with the component's own moved suite as the unchanged behavioral trace.
 * Generic strings are formatted with react-intl in `/ui`, per design D5.
 */
export const LinkEditorPopover = ({
  editor,
  initialHref,
  hasLink,
  onClose,
}: LinkEditorPopoverProps) => {
  const intl = useIntl();
  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<LinkForm>({ defaultValues: { href: initialHref } });
  const fieldId = useId();
  const messageId = `${fieldId}-message`;
  const error = errors.href;

  const removeLink = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    onClose();
  };

  const onSubmit = ({ href }: LinkForm) => {
    // Prefix a bare host with `https://` so it resolves absolutely rather than
    // against the session URL; `normalizeLinkHref` also trims.
    const normalized = normalizeLinkHref(href);
    if (!normalized) {
      removeLink();
      return;
    }
    // `setLink` returns false when the href fails the extension's protocol
    // allowlist (e.g. `javascript:`), leaving the document untouched — surface
    // that instead of closing on a silent no-op.
    const applied = editor
      .chain()
      .focus()
      .extendMarkRange('link')
      .setLink({ href: normalized })
      .run();
    if (!applied) {
      setError('href', {
        message: intl.formatMessage({
          defaultMessage: 'Enter a valid link',
          id: 'X5UXS6a70O',
        }),
      });
      return;
    }
    onClose();
  };

  return (
    <form
      className="flex items-center gap-2"
      onSubmit={(event) => void handleSubmit(onSubmit)(event)}
    >
      <div className="flex flex-1 flex-col gap-1">
        <Input
          {...register('href')}
          id={fieldId}
          autoFocus
          aria-label={intl.formatMessage({
            defaultMessage: 'Link URL',
            id: '1e9zwhbnvk',
          })}
          aria-invalid={!!error}
          aria-describedby={error ? messageId : undefined}
          placeholder="https://example.com"
          data-testid="link-editor-input"
        />
        {error?.message ? (
          <p id={messageId} className="text-sm font-medium text-validation-destructive">
            {error.message}
          </p>
        ) : null}
      </div>
      <Button type="submit" size="sm" data-testid="link-editor-apply">
        {intl.formatMessage({ defaultMessage: 'Apply', id: 'EWw/tKINJT' })}
      </Button>
      {hasLink && (
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={removeLink}
          data-testid="link-editor-remove"
        >
          {intl.formatMessage({ defaultMessage: 'Remove link', id: 'cr1jN833YB' })}
        </Button>
      )}
    </form>
  );
};
